import {useState} from 'react'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {AppDate, localEndOfDayIso, localStartOfDayIso} from '@/lib/date-utils'
import {MutedText} from '@/components/shared/MutedText'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {Input} from '@/components/ui/input'
import {DatePicker} from '@/components/shared/DatePicker'
import {Label} from '@/components/ui/label'
import {Switch} from '@/components/ui/switch'
import {DismissibleBadge} from '@/components/shared/DismissibleBadge'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {FormGrid} from '@/components/shared/FormGrid'
import {InlineRow} from '@/components/shared/InlineRow'
import {useToast} from '@/hooks/use-toast'
import {useAppConfig} from '@/hooks/use-app-config'
import type {DelegationCreateRequest} from '@/types/delegation'
import {Combobox} from '@/components/ui/combobox'
import type {ComboboxOption} from '@/components/ui/combobox'

const DEFAULT_STEP = 'ALL'

interface SelectedDeputy {
    id: string
    displayName: string
}

interface DelegationFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function DelegationFormDialog({open, onOpenChange}: DelegationFormDialogProps) {
    const services = useServices()
    const i18n = useI18n()
    const {toast} = useToast()
    const queryClient = useQueryClient()
    const {workflowSteps} = useAppConfig()

    const [selectedDeputies, setSelectedDeputies] = useState<SelectedDeputy[]>([])
    const [deputySearch, setDeputySearch] = useState('')
    const [stepCode, setStepCode] = useState(DEFAULT_STEP)
    const [permanent, setPermanent] = useState(false)
    const [startAt, setStartAt] = useState('')
    const [endAt, setEndAt] = useState('')
    const [reason, setReason] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const {data: identityResults} = useQuery({
        queryKey: ['identity-search', deputySearch],
        queryFn: () => services.identities.list({q: deputySearch, size: 10, lifecycleState: 'ACTIVE'}),
        enabled: deputySearch.length >= 2,
    })

    const deputyOptions: ComboboxOption[] = (identityResults?.content ?? [])
        .filter((id) => !selectedDeputies.some((d) => d.id === id.id))
        .map((id) => ({
            value: id.id,
            label: id.party.displayName,
        }))

    function addDeputy(id: string | null) {
        if (!id) return
        const identity = identityResults?.content.find((i) => i.id === id)
        if (!identity) return
        if (selectedDeputies.some((d) => d.id === id)) return
        setSelectedDeputies((prev) => [...prev, {id, displayName: identity.party.displayName}])
    }

    function removeDeputy(id: string) {
        setSelectedDeputies((prev) => prev.filter((d) => d.id !== id))
    }

    function resetForm() {
        setSelectedDeputies([])
        setDeputySearch('')
        setStepCode(DEFAULT_STEP)
        setPermanent(false)
        setStartAt('')
        setEndAt('')
        setReason('')
    }

    const canSubmit = selectedDeputies.length > 0 && (permanent || (startAt && endAt))

    async function handleSubmit() {
        if (!canSubmit) return
        setSubmitting(true)
        const errors: string[] = []
        let successCount = 0
        for (const deputy of selectedDeputies) {
            try {
                const req: DelegationCreateRequest = {
                    deputyIdentityId: deputy.id,
                    workflowStepCode: stepCode,
                    startAt: permanent ? null : localStartOfDayIso(startAt),
                    endAt: permanent ? null : localEndOfDayIso(endAt),
                    reason: reason || null,
                }
                await services.delegations.myCreate(req)
                successCount++
            } catch (err) {
                errors.push(i18n.delegations.createErrorForDeputy({deputy: deputy.displayName, error: (err as Error).message}))
            }
        }
        if (successCount > 0) {
            toast({description: i18n.delegations.createSuccess})
            queryClient.invalidateQueries({queryKey: ['my-delegations']})
        }
        if (errors.length > 0) {
            toast({description: errors.join('\n'), variant: 'destructive'})
        }
        if (errors.length === 0) {
            resetForm()
            onOpenChange(false)
        }
        setSubmitting(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{i18n.delegations.createDelegation}</DialogTitle>
                    <DialogDescription>{i18n.delegations.myDelegationsDesc}</DialogDescription>
                </DialogHeader>

                <FormGrid className="py-4">
                    <FormGrid>
                        <Label>{i18n.delegations.form.deputy}</Label>
                        <Combobox
                            options={deputyOptions}
                            value={null}
                            onValueChange={addDeputy}
                            onSearch={setDeputySearch}
                            placeholder={i18n.delegations.form.deputyPlaceholder as string}
                            data-testid="deputy-combobox"
                        />
                        {selectedDeputies.length > 0 && (
                            <InlineRow wrap gap="md" data-testid="selected-deputies">
                                {selectedDeputies.map((d) => (
                                    <DismissibleBadge
                                        key={d.id}
                                        onDismiss={() => removeDeputy(d.id)}
                                        data-testid={`remove-deputy-${d.id}`}
                                    >
                                        {d.displayName}
                                    </DismissibleBadge>
                                ))}
                            </InlineRow>
                        )}
                    </FormGrid>

                    <FormGrid>
                        <Label>{i18n.delegations.form.workflowStep}</Label>
                        <Select value={stepCode} onValueChange={setStepCode}>
                            <SelectTrigger data-testid="workflow-step-select">
                                <SelectValue/>
                            </SelectTrigger>
                            <SelectContent>
                                {workflowSteps.map((ws) => (
                                    <SelectItem key={ws.code} value={ws.code}>
                                        {ws.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </FormGrid>

                    <InlineRow gap="lg">
                        <Switch
                            checked={permanent}
                            onCheckedChange={setPermanent}
                            data-testid="permanent-switch"
                        />
                        <div>
                            <Label className="font-medium">{i18n.delegations.form.permanent}</Label>
                            <MutedText size="xs">
                                {i18n.delegations.form.permanentDescription}
                            </MutedText>
                        </div>
                    </InlineRow>

                    {!permanent && (
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label>{i18n.delegations.form.startAt}</Label>
                                <DatePicker
                                    testId="start-date-input"
                                    value={startAt}
                                    onChange={setStartAt}
                                    maxDate={endAt ? AppDate.ofLocalDateStr(endAt) : undefined}
                                    ariaLabel={String(i18n.delegations.form.startAt)}
                                />
                            </FormGrid>
                            <FormGrid>
                                <Label>{i18n.delegations.form.endAt}</Label>
                                <DatePicker
                                    testId="end-date-input"
                                    value={endAt}
                                    onChange={setEndAt}
                                    minDate={startAt ? AppDate.ofLocalDateStr(startAt) : undefined}
                                    ariaLabel={String(i18n.delegations.form.endAt)}
                                />
                            </FormGrid>
                        </DetailGrid>
                    )}

                    <FormGrid>
                        <Label>{i18n.delegations.form.reason}</Label>
                        <Input
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={i18n.delegations.form.reasonPlaceholder}
                            data-testid="reason-input"
                        />
                    </FormGrid>
                </FormGrid>

                <SaveCancelFooter
                    testIdPrefix="create"
                    onCancel={() => onOpenChange(false)}
                    saving={submitting}
                    disabled={!canSubmit}
                    saveLabel={i18n.delegations.createDelegation}
                    saveType="button"
                    onSave={handleSubmit}
                />
            </DialogContent>
        </Dialog>
    )
}
